import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  Business as BusinessIcon,
  MiscellaneousServices as ServicosIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
  TvOutlined as DisplayIcon,
  SensorsOutlined as SensorsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import wagService, { WAGDashboard } from '../../services/wag.service';

const WAGAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<WAGDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    wagService.getDashboard()
      .then(setDashboard)
      .catch(() => setError('Erro ao carregar dashboard WAG'))
      .finally(() => setLoading(false));
  }, []);

  const modules = [
    { title: 'Agências', desc: 'Gerir locais e pontos de atendimento', icon: <BusinessIcon />, path: '/wag/agencias', color: '#e94560' },
    { title: 'Serviços', desc: 'Configurar tipos de atendimento', icon: <ServicosIcon />, path: '/wag/servicos', color: '#6233A0' },
    { title: 'Operadores', desc: 'Gerir utilizadores com acesso à fila', icon: <PeopleIcon />, path: '/wag/operadores', color: '#0D7F9F' },
    { title: 'Display / TV', desc: 'Configurar painel de chamadas', icon: <DisplayIcon />, path: '/wag/display', color: '#10B981' },
  ];

  const planColor: Record<string, string> = {
    FREE: '#9CA3AF',
    BASIC: '#3B82F6',
    PRO: '#8B5CF6',
    ENTERPRISE: '#F59E0B',
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <SensorsIcon sx={{ fontSize: 32, color: '#e94560' }} />
        <Typography variant="h4" fontWeight={700}>WAG</Typography>
        {dashboard?.plano && (
          <Chip
            label={dashboard.plano}
            size="small"
            sx={{ bgcolor: planColor[dashboard.plano] + '22', color: planColor[dashboard.plano], fontWeight: 700 }}
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Sistema de Gestão de Atendimento — filas via QR Code
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats do dia */}
      {dashboard && (
        <Grid container spacing={2} mb={4}>
          {[
            { label: 'Agências', value: dashboard.totalAgencias, sub: 'configuradas', icon: <BusinessIcon />, color: '#e94560' },
            { label: 'Serviços', value: dashboard.totalServicos, sub: 'disponíveis', icon: <ServicosIcon />, color: '#6233A0' },
            { label: 'Senhas hoje', value: dashboard.senhasHoje, sub: 'emitidas', icon: <QrCodeIcon />, color: '#0D7F9F' },
            { label: 'Atendidos', value: dashboard.atendidosHoje, sub: 'hoje', icon: <CheckIcon />, color: '#10B981' },
            { label: 'Desistências', value: dashboard.desistenciasHoje, sub: 'hoje', icon: <TimeIcon />, color: '#F59E0B' },
            { label: 'Espera média', value: `${dashboard.tempoEsperaMedioMin ?? '—'} min`, sub: 'hoje', icon: <TimeIcon />, color: '#EF4444' },
          ].map((stat) => (
            <Grid item xs={6} sm={4} md={2} key={stat.label}>
              <Paper sx={{ p: 2, borderRadius: 3, borderLeft: `4px solid ${stat.color}` }}>
                <Box display="flex" alignItems="center" gap={1} mb={1} color={stat.color}>{stat.icon}</Box>
                <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label} · {stat.sub}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Módulos */}
      <Grid container spacing={3}>
        {modules.map((mod) => (
          <Grid item xs={12} sm={6} md={3} key={mod.title}>
            <Card
              sx={{ cursor: 'pointer', borderRadius: 3, transition: 'transform .2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
              onClick={() => navigate(mod.path)}
            >
              <CardContent>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: mod.color + '22', color: mod.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  {mod.icon}
                </Box>
                <Typography variant="h6" fontWeight={600}>{mod.title}</Typography>
                <Typography variant="body2" color="text.secondary">{mod.desc}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WAGAdminDashboard;
